import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GridStack } from 'gridstack';
import { GRID_COLUMNS } from '@tallyframe/core';
import { WidgetFrame } from './WidgetFrame.js';
import { BrandContext, brandStyle, resolveBrand } from './brand.js';
import { themeStyle } from './theme-style.js';
/** Pixel height of one grid row, and the gap around each widget. Chosen so the
 *  shipped fixtures read correctly: a 3-row KPI is a compact card, an 8-row
 *  chart is tall enough to label, a 10-row table shows about a dozen rows. */
export const CELL_HEIGHT = 44;
export const CELL_MARGIN = 8;
/**
 * The 6-column re-layout: scale each widget's width by the column ratio when
 * that lands on a whole column, else keep its width (capped at the column
 * count), then flow the widgets in reading order, wrapping a line when the
 * next one would not fit. Three `w: 4` KPIs become one row of three `w: 2`
 * tiles, two `w: 6` charts stay two across, and four `w: 3` KPIs fold into
 * two clean rows of two.
 *
 * It assumes it is placing every node from the 12-column layout, into an
 * empty grid: it flows from x 0, y 0 and ignores anything already in
 * `newNodes`. The sync path guarantees that today: it always writes the
 * 12-column coordinates and narrows from 12.
 *
 * Strict reading-order flow is deliberately line-based. Two widgets stacked
 * to the right of a tall one lay out as: the first beside it, the second
 * wrapped to x 0 under the tall one. `float: false` does not close the space
 * left beside the tall widget, because it only moves widgets straight up
 * within their own columns. That gap is accepted, the same trade-off
 * `'list'` made. Nothing in the shipped fixtures stacks widgets this way.
 * Don't add column tracking here without a fixture that needs it.
 *
 * GridStack's own `'moveScale'` rounds both x and w independently, so four
 * `w: 3` KPIs (x 0/3/6/9) land on x 0/2/3/5 at `w: 2` — colliding and
 * overflowing; `'list'` never scales, so three `w: 4` KPIs stack one per row.
 */
const scaleIntoRows = (column, oldColumn, newNodes, nodes) => {
    const ratio = column / oldColumn;
    flowInReadingOrder(column, newNodes, nodes, (node) => {
        const w = node.w ?? 1;
        const scaled = w * ratio;
        return Number.isInteger(scaled) ? scaled : w;
    });
};
/**
 * The phone re-layout, 1 column: every widget takes the full row, stacked in
 * reading order. Nothing is reordered. KPIs used to pair up two per row, but
 * at phone width a half-row KPI cuts its title and value short (revision 3,
 * amended 2026-10-20). The same flow rules and assumptions as
 * `scaleIntoRows` apply.
 */
const stackInReadingOrder = (column, _oldColumn, newNodes, nodes) => {
    flowInReadingOrder(column, newNodes, nodes, () => column);
};
/** Places `nodes` in (y, x) order from x 0, y 0, each at `widthOf(node)`
 *  (clamped to 1..column), wrapping a line when the next would not fit. */
function flowInReadingOrder(column, newNodes, nodes, widthOf) {
    const ordered = [...nodes].sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0));
    let x = 0;
    let lineY = 0;
    let lineBottom = 0;
    for (const node of ordered) {
        const width = Math.min(column, Math.max(1, widthOf(node)));
        if (x > 0 && x + width > column) {
            x = 0;
            lineY = lineBottom;
        }
        node.x = x;
        node.y = lineY;
        node.w = width;
        x += width;
        lineBottom = Math.max(lineBottom, lineY + (node.h ?? 1));
        newNodes.push(node);
    }
}
/**
 * View-mode breakpoints: inclusive upper bounds on the `.grid-stack` width
 * inside the surface padding. Wider than 960 → 12, 601-960 → 6, 600 and
 * below → 1, one widget per row.
 */
const VIEW_BREAKPOINTS = [
    { w: 600, c: 1, layout: stackInReadingOrder },
    { w: 960, c: 6, layout: scaleIntoRows }
];
/** The re-layout GridStack itself applies when it narrows to `column`. */
function breakpointLayout(column) {
    return VIEW_BREAKPOINTS.find((b) => b.c === column)?.layout ?? 'list';
}
/** Reads the grid's current placements straight out of GridStack's engine. */
function readLayout(grid) {
    const items = [];
    for (const node of grid.engine.nodes) {
        if (typeof node.id !== 'string')
            continue;
        items.push({
            widgetId: node.id,
            x: node.x ?? 0,
            y: node.y ?? 0,
            w: node.w ?? 1,
            h: node.h ?? 1
        });
    }
    return items;
}
/** Tab text for a widget's kind (spec §6.3). Rendered on *every* interactive
 *  widget, selected or not — the tab carries the `.tf-drag-handle` grip, so
 *  gating it on selection would take the grip away from every widget that
 *  isn't currently selected. */
const KIND_TAB = { kpi: 'KPI', chart: 'Chart', table: 'Table', text: 'Note' };
function GridItem(props) {
    const { view, interactive, selected } = props;
    if (!interactive) {
        return (_jsx("div", { className: "tf-item", "data-widget-id": view.widget.id, children: _jsx(WidgetFrame, { view: view, animate: true }) }));
    }
    const onKeyDown = (event) => {
        if (event.target !== event.currentTarget)
            return;
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            props.onSelect();
        }
    };
    return (_jsxs("div", { className: selected ? 'tf-item tf-item--selectable tf-item--selected' : 'tf-item tf-item--selectable', "data-widget-id": view.widget.id, "data-selected": selected ? 'true' : 'false', tabIndex: 0, "aria-label": `${view.widget.title} widget`, "aria-current": selected ? 'true' : undefined, onClick: props.onSelect, onKeyDown: onKeyDown, children: [_jsxs("span", { className: "tf-item-tab", "aria-hidden": "true", children: [_jsx("span", { className: "tf-drag-handle", title: "Drag to move", children: "\u283F" }), KIND_TAB[view.kind]] }), _jsx(WidgetFrame, { view: view, animate: true })] }));
}
/**
 * The 12-column dashboard surface, shared by `ReportViewer` and `ReportEditor`
 * so the two cannot drift apart visually.
 *
 * GridStack and React are kept strictly out of each other's way: GridStack owns
 * the `.grid-stack-item` wrappers and does nothing but position them, while
 * React renders every widget through a portal into the stable
 * `.grid-stack-item-content` node inside. React never rewrites a `gs-*`
 * attribute, and GridStack never touches a node React is reconciling.
 *
 * Layout changes arriving from outside (an undo, a concurrent edit) are pushed
 * into GridStack with the change listeners suppressed, so a re-sync can never
 * echo back as another `set_layout` operation — and never while the pointer
 * holds a gesture open, because writing into a node GridStack is mid-gesture
 * on destroys the state its own teardown needs and loses the gesture whole.
 */
export function ReportGrid(props) {
    const { definition, views, interactive, selectedWidgetId = null } = props;
    const containerRef = useRef(null);
    const gridRef = useRef(null);
    const nodesRef = useRef(new Map());
    const syncingRef = useRef(false);
    // True between `dragstart`/`resizestart` and the matching stop event: the
    // pointer owns a node and GridStack is mid-gesture on it.
    const gestureRef = useRef(false);
    // A definition change that arrived while `gestureRef` was set, and so was
    // skipped. Replayed from the stop handler.
    const pendingSyncRef = useRef(false);
    // The most recent layout sync, so the stop handler can replay it without
    // waiting for another render.
    const syncLayoutRef = useRef(null);
    const onLayoutChangeRef = useRef(props.onLayoutChange);
    onLayoutChangeRef.current = props.onLayoutChange;
    const [hosts, setHosts] = useState({});
    // Placements the grid should show right now, restricted to widgets that
    // actually exist — a layout entry for a deleted widget renders nothing.
    const placements = useMemo(() => {
        const widgetIds = new Set(definition.widgets.map((w) => w.id));
        const map = new Map();
        for (const item of definition.layout.items) {
            if (widgetIds.has(item.widgetId))
                map.set(item.widgetId, item);
        }
        return map;
    }, [definition.layout, definition.widgets]);
    // Cheap value key: re-run the sync effect when any coordinate actually moves,
    // not merely when the host hands us a new object identity.
    const placementKey = useMemo(() => [...placements.values()]
        .map((i) => `${i.widgetId}:${i.x},${i.y},${i.w},${i.h}`)
        .sort()
        .join('|'), [placements]);
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const grid = GridStack.init({
            column: GRID_COLUMNS,
            cellHeight: CELL_HEIGHT,
            margin: CELL_MARGIN,
            float: false,
            animate: false,
            acceptWidgets: false,
            staticGrid: !interactive,
            disableDrag: !interactive,
            disableResize: !interactive,
            handle: '.tf-drag-handle',
            resizable: { handles: 'se,sw,e,s' },
            minRow: 1,
            // View mode resolves its column count from `VIEW_BREAKPOINTS`. Edit
            // mode always holds 12: a drag gesture must land at the coordinates
            // it was dropped at, not at coordinates scaled for whatever width
            // the editor happens to be rendered at.
            //
            // GridStack picks the column count from the grid's own width both in
            // its constructor (`checkDynamicColumn()`, before any widget exists)
            // and on every ResizeObserver tick (`onResize`), so an initial mount
            // and a later resize to the same width land on the same count.
            //
            // `columnMax` has to be explicit: GridStack only defaults it to 12
            // when there's at most one breakpoint (gridstack.js checkDynamicColumn
            // et al.) — with two, a resize past the widest breakpoint would
            // otherwise call `column(undefined)`, a no-op, and the grid would
            // never widen back out. The top-level `'moveScale'` is what widening
            // back to 12 (no matching breakpoint) falls through to, and it's the
            // one that actually restores the cached 12-column layout — 'list'
            // would just re-flow the narrow positions instead.
            ...(interactive
                ? {}
                : {
                    columnOpts: {
                        breakpointForWindow: false,
                        columnMax: GRID_COLUMNS,
                        breakpoints: VIEW_BREAKPOINTS,
                        layout: 'moveScale'
                    }
                })
        }, container);
        if (!grid)
            return;
        gridRef.current = grid;
        const begin = () => {
            gestureRef.current = true;
        };
        const emit = () => {
            gestureRef.current = false;
            // Report the gesture's own geometry before replaying anything that was
            // held back, so the operation carries what the operator just did.
            if (!syncingRef.current)
                onLayoutChangeRef.current?.(readLayout(grid));
            if (pendingSyncRef.current) {
                pendingSyncRef.current = false;
                syncLayoutRef.current?.();
            }
        };
        // `grid.on()` keeps a single handler per event name
        // (`_gsEventHandler[name] = callback`), so these four registrations must
        // stay the only ones in the component.
        grid.on('dragstart', begin);
        grid.on('resizestart', begin);
        grid.on('dragstop', emit);
        grid.on('resizestop', emit);
        return () => {
            grid.off('dragstart');
            grid.off('resizestart');
            grid.off('dragstop');
            grid.off('resizestop');
            gestureRef.current = false;
            pendingSyncRef.current = false;
            syncLayoutRef.current = null;
            // `false`: leave the DOM alone. React owns the container node and will
            // remove it itself; letting GridStack remove it too throws.
            grid.destroy(false);
            gridRef.current = null;
            nodesRef.current.clear();
            setHosts({});
        };
    }, [interactive]);
    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (!grid)
            return;
        // Writes every placement's true 12-column coordinates onto its existing
        // GridStack node. Only ever called while the grid is actually showing 12
        // columns, so `update()` writes them through untouched — no clamping.
        // Processed in (y, x) order: each `update()` still resolves collisions
        // against whatever hasn't moved yet, so an item that ends up in another
        // item's old spot must be written before that other item claims its own
        // new one, or the later write can land on top of it.
        const writePositions = () => {
            const ordered = [...placements.values()].sort((a, b) => a.y - b.y || a.x - b.x);
            for (const item of ordered) {
                const existing = nodesRef.current.get(item.widgetId);
                if (!existing)
                    continue;
                const node = existing.gridstackNode;
                if (!node || node.x !== item.x || node.y !== item.y || node.w !== item.w || node.h !== item.h) {
                    grid.update(existing, { x: item.x, y: item.y, w: item.w, h: item.h });
                }
            }
        };
        const runSync = () => {
            syncingRef.current = true;
            let structureChanged = false;
            grid.batchUpdate();
            try {
                for (const [widgetId, element] of [...nodesRef.current]) {
                    if (placements.has(widgetId))
                        continue;
                    grid.removeWidget(element, true, false);
                    nodesRef.current.delete(widgetId);
                    structureChanged = true;
                }
                // New widgets always need adding, regardless of the current column
                // count — GridStack clamps their position and width to whatever it's
                // currently showing.
                for (const item of placements.values()) {
                    if (nodesRef.current.has(item.widgetId))
                        continue;
                    const element = grid.addWidget({ id: item.widgetId, x: item.x, y: item.y, w: item.w, h: item.h });
                    if (element) {
                        nodesRef.current.set(item.widgetId, element);
                        structureChanged = true;
                    }
                }
            }
            finally {
                grid.batchUpdate(false);
            }
            const startColumn = grid.getColumn();
            if (!interactive && startColumn !== GRID_COLUMNS) {
                // The grid is rendering fewer columns than the definition's 12-column
                // coordinates assume — a responsive breakpoint, or a column forced for
                // a test. Writing those coordinates straight through `update()` while
                // still narrow would push out-of-range x/w into the grid and leave
                // widgets overlapping, since GridStack only ever compacts cleanly
                // through its own column-change reflow (the same one a real resize
                // back down to this width would trigger). Briefly widen to 12 columns
                // to write the coordinates cleanly, then narrow back down with the
                // same re-layout the breakpoint itself uses. A hard-coded 'list'
                // here would undo the 6-column scaling on the initial mount (which
                // runs through this path) and on every later definition change.
                grid.column(GRID_COLUMNS);
                writePositions();
                grid.column(startColumn, breakpointLayout(startColumn));
            }
            else {
                writePositions();
            }
            syncingRef.current = false;
            if (!structureChanged)
                return;
            const next = {};
            for (const [widgetId, element] of nodesRef.current) {
                const content = element.querySelector('.grid-stack-item-content');
                if (content)
                    next[widgetId] = content;
            }
            setHosts(next);
        };
        // Kept current even when the sync is skipped below, so a gesture that
        // outlives several skipped changes replays the newest placements rather
        // than a stale closure.
        syncLayoutRef.current = runSync;
        // Never write into a node the pointer owns. `grid.update()` ends with
        // `delete n._orig`, and GridStack's gesture teardown `onEndMoving` opens
        // with `node.w !== node._orig.w` — so a sync landing mid-gesture makes
        // mouse-up throw *before* GridStack dispatches `dragstop`/`resizestop`,
        // and the gesture is discarded with no operation and no error the
        // operator can see. The gesture's own layout self-corrects; anything else
        // the change carried is replayed from the stop handler.
        if (gestureRef.current) {
            pendingSyncRef.current = true;
            return;
        }
        runSync();
    }, [placements, placementKey, interactive]);
    const onSelect = props.onSelectWidget;
    const handleBackgroundClick = useCallback((event) => {
        if (!interactive || !onSelect)
            return;
        const target = event.target;
        if (target?.closest('.tf-item') || target?.closest('.tf-report-header'))
            return;
        onSelect(null);
    }, [interactive, onSelect]);
    const className = ['tf-surface', props.className].filter(Boolean).join(' ');
    const hasWidgets = definition.widgets.length > 0;
    // Keyed on primitive fields rather than object identity, so a host that
    // passes a fresh `{ accent: ... }` literal every render does not re-derive
    // the palette and re-provide `BrandContext` on every keystroke elsewhere.
    const resolvedBrand = useMemo(() => resolveBrand(props.brand), [props.brand?.accent, props.brand?.name, props.brand?.logoUrl, props.brand?.theme]);
    const style = useMemo(() => ({ ...brandStyle(resolvedBrand), ...(props.brand?.theme ? themeStyle(props.brand.theme) : {}) }), [resolvedBrand, props.brand?.theme]);
    return (_jsx(BrandContext.Provider, { value: resolvedBrand, children: _jsxs("div", { className: className, style: style, onClick: handleBackgroundClick, children: [props.header, _jsx("div", { ref: containerRef, className: "grid-stack tf-grid" }), hasWidgets ? null : _jsx("p", { className: "tf-empty", children: "This report has no widgets yet." }), definition.widgets.map((widget) => {
                    const host = hosts[widget.id];
                    const view = views.get(widget.id);
                    if (!host || !view)
                        return null;
                    return createPortal(_jsx(GridItem, { view: view, interactive: interactive, selected: selectedWidgetId === widget.id, onSelect: () => onSelect?.(widget.id) }), host, widget.id);
                })] }) }));
}
//# sourceMappingURL=ReportGrid.js.map